import { useEffect, useRef } from 'react'

import * as React from 'react'
import { stylesheet } from 'typestyle'

/* Hooks ======================================================================================== */
import useValue from '../../hooks/use-value'

/* Model ======================================================================================== */
import Marker from './google-marker'
import Circle from './google-circle'

/* Constants ==================================================================================== */
import OPTIONS from './google-map.options'

/* Components =================================================================================== */
import { Box } from '../box/box.component'

/* <GooglMap /> ================================================================================= */
export type LocationType = { lat: number; lng: number }
export const GoogleAPI: any = {}

let debounceOnCenterChange: any

const markers: any = []
let circle: any
let map: any
let loaded = false
export const GoogleMap: React.FC<{
  active: boolean
  center?: LocationType
  children?: any
  zoom?: number
  locations?: any
  onChange?: (position: any) => void
  onZoom?: (position: any) => void
  onLoad?: (position: any) => void
  marker?: any
  centerMarker?: any
  google?: any
  clear?: any
  radius?: any
  $bounds?: any
  trigger?: any
  pan?: any
  lastTriggered?: any
  options?: any
}> = ({
  center,
  onChange,
  zoom,
  locations,
  marker,
  google,
  radius,
  $bounds,
  pan,
  centerMarker,
  children,
  lastTriggered,
  options,
}) => {
  const mapRef = useRef(null)
  const [_center] = useValue(center)
  const [_lastTriggered, $_lastTriggered] = useValue(lastTriggered)
  const [_locations, $_locations] = useValue(locations)

  Marker.prototype = new google.maps.OverlayView()
  Circle.prototype = new google.maps.OverlayView()

  useEffect(() => {
    return () => {
      loaded = false
    }
  }, [])

  useEffect(() => {
    if (map && zoom) {
      map.setZoom(zoom)
    }
  }, [zoom])

  useEffect(() => {
    if (!loaded) {
      initialize(_center)
    } else {
      if (map) {
        map.panTo(_center)
      }
    }
  }, [_center])

  useEffect(() => {
    if (map && pan) {
      map.panTo(pan)
    }
  }, [pan])

  useEffect(() => {
    if (lastTriggered) {
      $_lastTriggered.set(lastTriggered)
      $_locations.set(locations)
    }
  }, [lastTriggered, locations])

  useEffect(() => {
    if (map) {
      let centerMarkerCount = 0
      markers.map((marker: any) => {
        if (!marker.isCenter) {
          // if (lastTriggered) {
          marker.setMap(null)
          // }
        } else {
          if (centerMarker && lastTriggered) {
            marker.setMap(null)
          }
          centerMarkerCount++
        }
      })

      if ((centerMarker && lastTriggered) || centerMarkerCount === 0) {
        markers.push(
          new Marker({
            component: React.cloneElement(centerMarker, {
              index: -1,
              focused: false,
            }),
            location: {
              children: [],
              location: {
                coordinates: [center?.lng, center?.lat],
                type: 'Point',
              },
            } as any,
            isCenter: true,
            map,
            index: -1,
          }),
        )
      }
      if (locations && locations.length > 0) {
        locations.map((loc: any) => {
          const { location, index } = loc

          if (location) {
            markers.push(
              new Marker({
                component: React.cloneElement(marker, {
                  index,
                  focused: location.focused,
                  isAnimating: true,
                }),
                location: location as any,
                map,
                index,
              }),
            )
          }
        })
      }
    }
  }, [locations, markers, _lastTriggered, lastTriggered])

  const initialize = (_center: any) => {
    if (_center) {
      loaded = true
      map = new (google.maps.Map as any)(document.getElementById('map'), {
        ...OPTIONS,
        ...options,
        center: _center,
      })

      circle = new google.maps.Circle({
        strokeColor: 'black',
        strokeOpacity: 0.8,
        strokeWeight: 0,
        fillColor: 'rgba(0,0,0,0)',
        fillOpacity: 0.35,
        map,
        center,
        radius: radius,
      })

      const handleChange = () => {
        const _center = map.getCenter()
        const bounds = map.getBounds()

        // $map.set(map)

        if (bounds) {
          $bounds.set({
            lat1: bounds.getSouthWest().lat(),
            lat2: bounds.getNorthEast().lat(),
            lng1: bounds.getSouthWest().lng(),
            lng2: bounds.getNorthEast().lng(),
          })
          const mapTop: any = new google.maps.LatLng(bounds.getNorthEast().lat(), _center.lng())
          const distanceToTop: any = google.maps.geometry.spherical.computeDistanceBetween(
            _center,
            mapTop,
          )

          // Find map left border & distance to center
          const mapLeft = new google.maps.LatLng(_center.lat(), bounds.getSouthWest().lng())
          const distanceToLeft = google.maps.geometry.spherical.computeDistanceBetween(
            _center,
            mapLeft,
          )

          // Get shortest length
          const radius = distanceToTop < distanceToLeft ? distanceToTop : distanceToLeft
          circle.setRadius(radius)

          if (onChange) {
            onChange({
              radius,
              center: {
                lat: _center.lat(),
                lng: _center.lng(),
              },
              location: {
                lat1: bounds.getSouthWest().lat(),
                lat2: bounds.getNorthEast().lat(),
                lng1: bounds.getSouthWest().lng(),
                lng2: bounds.getNorthEast().lng(),
              },
            })
          }
        }
      }

      map.addListener('idle', () => {
        if (debounceOnCenterChange) {
          clearTimeout(debounceOnCenterChange)
        }

        debounceOnCenterChange = setTimeout(handleChange, 250)
      })

      map.addListener('bounds_changed', () => {
        if (debounceOnCenterChange) {
          clearTimeout(debounceOnCenterChange)
        }
        debounceOnCenterChange = setTimeout(handleChange, 1000)
      })
    }
  }

  return (
    <Box fullHeight fullWidth>
      <div className={STYLES.container} ref={mapRef} id={'map'} />
      {children}
    </Box>
  )
}

const STYLES = stylesheet({
  container: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
})

/* Export ======================================================================================= */
export default GoogleMap
